import { getTerraMapDynamicViewerOptions, groupBy, IDataLayerType, ILayerViewModel, isArrayEmpty, labels, LayerTypeGroupId, models, TerramapDynamicLayerViewer } from "../omnia"
import { InputLayer } from "./types"

export type SelectableLayer = ReturnType<typeof getSelectableLayers>[number]

export function getSelectableLayers(layers: ILayerViewModel[], fieldId: number) {

    const groups = createGroups(layers, fieldId)

    const types = groups
        .map(t => ({
            layers: t.values,
            text: getGroupName(t.key.type, t.values),
            typeId: t.key.typeId,
            group: t.key.group
        }))
        .sort((a, b) =>
            (a.group.group - b.group.group)
            || (a.group.subgroup - b.group.subgroup)
            || (a.typeId - b.typeId)
            || (b.group.year - a.group.year)
            || (a.text.localeCompare(b.text)))
        .map(a => ({
            layers: a.layers.sort((a, b) => (b.year || 0) - (a.year || 0)),
            group: a.group.text,
            year: a.group.year,
            text: a.text
        }))

    // Flatten groups and set viewers

    const selectableLayers = types
        .flatMap(type => type.layers.map(layer => {

            const viewer = layer.terramap != models.TerraMapLayerType.RawDynamicScale
                ? undefined
                : TerramapDynamicLayerViewer.factory(layer.typeId, layer.type.Name, getTerraMapDynamicViewerOptions(layer.type))

            const name = layer.description + (
                type.year
                    ? ` (${type.year})`
                    : ""
            )

            const inputLayer: InputLayer = {
                layer: layer.layer.datalayer,
                name,
                viewer
            }

            return {
                group: type.group,
                text: name,
                layer: inputLayer
            }

        }))

    return selectableLayers

}

function createGroups(layers: ILayerViewModel[], fieldId: number) {

    // Include all variants (i.e., dynamic scale layers),
    // and extract various properties used at the next stage

    const tmp = layers
        .filter(l => l.fieldId === fieldId)
        .flatMap(l => {
            const v = l.getVariants()
            return isArrayEmpty(v)
                ? [l]
                : [l].concat(v)
        })
        .map(l => ({
            layer: l,
            type: l.type,
            description: l.description,
            year: l.datalayer.FirstYearId ?? 0,
            typeId: l.typeId,
            terramap: l.datalayer.TerraMap
        }))

    // Group by type

    let groups = groupBy(
        tmp,
        l => ({
            type: l.type,
            typeId: (l.type ? l.type.TypeId : null) || 0,
            group: getGroupInfo(l.type),
        }),
        t => t.group.key
    )

    // Clone groups by year

    groups = groups
        .flatMap(group => {

            const years = group.values.map(l => l.year).unique(y => y)

            return years
                .sort((a, b) => b - a)
                .map(year => {

                    return {
                        key: {
                            ...group.key,
                            group: cloneGroupInfoForYear(group.key.group, year)
                        },
                        values: group.values.filter(l => l.year === year)
                    }

                })

        })

    return groups

}

function getGroupName(type: IDataLayerType, layers: { description: string }[]) {

    if ((!type) || (layers.length == 1)) {
        return layers[0].description
    }

    return type.Name

}

function cloneGroupInfoForYear(info: GroupInfo, year: number) {

    let { text, key } = info

    if (year) {
        text = `${text} (${year})`
        key = `${key}\u0000${year}`
    }

    return {
        ...info,
        text,
        key,
        year
    }

}

type GroupInfo = ReturnType<typeof getGroupInfo>

function getGroupInfo(type: IDataLayerType) {

    const group = (type ? type.GroupId : null) || LayerTypeGroupId.Other
    const subgroup = (type ? type.SubgroupId : null) || 0

    const groupText = labels.LayerTypeGroupId(group)
    const subgroupText = subgroup ? labels.LayerTypeSubgroupId(subgroup) : null

    const text = [groupText, subgroupText].filter(t => !!t).join(" - ")

    const key = String(type?.TypeId)

    return { group, subgroup, text, key, year: 0 }

}
